<template>
    <div class="role-edit-container animated fadeIn">
        <!-- Loading Skeleton -->
        <div v-if="loading" class="loading-state">
            <b-card class="role-basic-info mb-4">
                <b-skeleton animation="throb" width="30%" height="24px" class="mb-4"></b-skeleton>
                <b-row>
                    <b-col md="6">
                        <b-skeleton animation="throb" width="20%" height="16px" class="mb-2"></b-skeleton>
                        <b-skeleton animation="throb" height="46px" class="mb-3"></b-skeleton>
                    </b-col>
                    <b-col md="6">
                        <b-skeleton animation="throb" width="20%" height="16px" class="mb-2"></b-skeleton>
                        <b-skeleton animation="throb" height="46px" class="mb-3"></b-skeleton>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col md="6">
                        <b-skeleton animation="throb" width="25%" height="16px" class="mb-2"></b-skeleton>
                        <b-skeleton animation="throb" height="38px" class="mb-3"></b-skeleton>
                    </b-col>
                    <b-col md="6">
                        <b-skeleton animation="throb" width="15%" height="16px" class="mb-2"></b-skeleton>
                        <b-skeleton animation="throb" height="38px" class="mb-3"></b-skeleton>
                    </b-col>
                </b-row>
            </b-card>

            <b-card class="permissions-card">
                <div class="d-flex justify-content-between align-items-center mb-4">
                    <b-skeleton animation="throb" width="20%" height="24px"></b-skeleton>
                    <b-skeleton animation="throb" width="15%" height="32px"></b-skeleton>
                </div>
                <b-skeleton animation="throb" height="46px" class="mb-4"></b-skeleton>
                <div class="components-grid">
                    <div v-for="i in 6" :key="i" class="component-card-skeleton">
                        <b-skeleton animation="throb" height="60px" class="mb-2"></b-skeleton>
                        <b-skeleton animation="throb" height="120px"></b-skeleton>
                    </div>
                </div>
            </b-card>
        </div>

        <!-- Actual Content -->
        <div v-else>
        <!-- Basic Info Card -->
        <b-card class="role-basic-info mb-4">
            <h4 class="mb-4">{{ $t('roleInformation') }}</h4>
            <b-row>
                <b-col md="6">
                    <b-form-group :label="this.$t('name')" label-for="name">
                        <b-form-input id="name" type="text" v-model="name" size="lg"></b-form-input>
                    </b-form-group>
                </b-col>
                <b-col md="6">
                    <b-form-group :label="this.$t('level')" label-for="level" :description="$t('roleLevelDescription')">
                        <b-form-input id="level" type="number" v-model.number="level" size="lg"></b-form-input>
                    </b-form-group>
                </b-col>
            </b-row>
            
            <b-row>
                <b-col md="6">
                    <b-form-group :label="this.$t('workspaces')" label-for="workspaces">
                        <v-select id="workspaces" v-model="spaces" :options="workspaces" :reduce="o => o.code" multiple></v-select>
                    </b-form-group>
                </b-col>
                <b-col md="6">
                    <b-form-group :label="this.$t('tags')" label-for="tags">
                        <v-select id="tags" v-model="tags" :options="[]" taggable multiple push-tags :no-drop="true" :placeholder="$t('addTags')"></v-select>
                    </b-form-group>
                </b-col>
            </b-row>

            <div class="text-right">
                <b-button variant="success" size="lg" @click="save">
                    <b-icon icon="check-circle"></b-icon> {{ $t('save') }}
                </b-button>
            </div>
        </b-card>

        <!-- Permissions Card -->
        <b-card class="permissions-card">
            <div class="permissions-header mb-4">
                <h4>{{ $t('permissions') }}</h4>
                <div class="header-actions">
                    <b-button variant="primary" size="sm" @click="selectAllPermissions" class="mr-2">
                        <b-icon icon="check-all"></b-icon> {{ $t('selectAll') }}
                    </b-button>
                    <b-badge variant="primary" pill>{{ perms.length }} {{ $t('selected') }}</b-badge>
                </div>
            </div>

            <!-- Search -->
            <b-form-input 
                v-model="filter" 
                :placeholder="$t('searchPermissions')" 
                class="mb-4"
                size="lg"
            >
                <template #prepend>
                    <b-icon icon="search"></b-icon>
                </template>
            </b-form-input>

            <!-- Category Tabs -->
            <div class="tabs-container mb-4">
                <button 
                    class="scroll-button scroll-left" 
                    @click="scrollTabs('left')"
                    v-show="showLeftArrow"
                >
                    <b-icon icon="chevron-left"></b-icon>
                </button>
                
                <b-tabs ref="permissionsTabs" v-model="activeTab" class="permissions-tabs" content-class="mt-4" nav-wrapper-class="tabs-nav">
                        <b-tab v-for="(permissions, group) in filteredPermissionsList" :key="group" :title="formatGroupName(group)" :title-link-class="'tab-' + group">
                            <template #title>
                                <span class="tab-title">
                                    {{ formatGroupName(group) }}
                                    <b-badge variant="light" class="ml-2">{{ getGroupPermCount(group) }}</b-badge>
                                </span>
                            </template>

                            <!-- Component Sections -->
                            <div class="components-grid">
                        <div 
                            v-for="(permList, component) in permissions" 
                            :key="component"
                            class="component-card"
                            :class="{ 'has-selected': hasSelectedPerms(group, component) }"
                        >
                            <div class="component-header" @click="toggleAllComponentPerms(group, component, permList)">
                                <div class="component-info">
                                    <h6 class="component-name">{{ formatComponentName(component) }}</h6>
                                    <span class="component-count">{{ getSelectedCount(group, component, permList) }}/{{ permList.length }}</span>
                                </div>
                                <b-form-checkbox
                                    :checked="areAllSelected(group, component, permList)"
                                    :indeterminate="isSomeSelected(group, component, permList)"
                                    @change="toggleAllComponentPerms(group, component, permList)"
                                    class="select-all-checkbox"
                                ></b-form-checkbox>
                            </div>

                            <div class="permissions-list">
                                <div 
                                    v-for="perm in permList" 
                                    :key="perm"
                                    class="permission-item"
                                    @click="togglePerm(perm, component)"
                                >
                                    <b-form-checkbox
                                        :checked="isPermSelected(perm, component)"
                                        @change="togglePerm(perm, component)"
                                        class="permission-checkbox"
                                    >
                                        <span class="permission-label">{{ formatPermissionName(perm) }}</span>
                                    </b-form-checkbox>
                                </div>
                            </div>
                        </div>
                    </div>
                </b-tab>
            </b-tabs>
                
                <button 
                    class="scroll-button scroll-right" 
                    @click="scrollTabs('right')"
                    v-show="showRightArrow"
                >
                    <b-icon icon="chevron-right"></b-icon>
                </button>
            </div>

            <!-- Selected Permissions Summary -->
            <div class="selected-permissions" v-if="perms.length > 0">
                <div class="summary-header">
                    <h5>{{ $t('selectedPermissions') }}</h5>
                    <b-button variant="outline-danger" size="sm" @click="clearAllPerms">
                        {{ $t('clearAll') }}
                    </b-button>
                </div>
                <div class="permissions-badges">
                    <b-badge 
                        v-for="perm in perms" 
                        :key="perm"
                        variant="success" 
                        class="perm-badge"
                        @click="removePermFromList(perm)"
                    >
                        {{ perm }}
                        <b-icon icon="x" class="ml-1"></b-icon>
                    </b-badge>
                </div>
            </div>
        </b-card>
        </div>
    </div>
</template>

<script>
import _ from "lodash"
import VSelect from "vue-select"
import { Switch as cSwitch } from '@coreui/vue'

export default {
    name: "Edit",

    components: {
        VSelect,
        cSwitch,
    },

    data: function () {
        return {
            name: "",
            level: null,
            perms: [],
            spaces: [],
            tags: [],
            activeTab: 0,
            filter: '',
            loading: true,
            showLeftArrow: false,
            showRightArrow: false
        }
    },
    computed: {
        permissionsList() {
            return this.$store.getters.permissionsList
        },

        forgeAppMap() {
            const apps = this.$store.getters.getAllForgeApps
            const map = {}
            for (const app of apps) {
                map[app.uuid] = app.name || app.label?.locales?.en || app.uuid
            }
            return map
        },

        groups() {
            return Object.keys(this.permissionsList)
        },

        workspaces() {
            return this.$store.getters.getCurrentProject.workspaces.map(w => {
                return {
                    label: w.name,
                    code: w.uuid
                }
            })
        },

        filteredPermissionsList() {
            if (!this.filter) return this.permissionsList
            
            const searchLower = this.filter.toLowerCase()
            const filtered = {}
            
            Object.keys(this.permissionsList).forEach(group => {
                const groupPerms = this.permissionsList[group]
                const filteredGroup = {}
                
                Object.keys(groupPerms).forEach(component => {
                    const componentPerms = groupPerms[component]
                    const matchingPerms = componentPerms.filter(perm => {
                        const fullPerm = `${perm} ${component}`.toLowerCase()
                        const formattedPerm = this.formatPermissionName(perm).toLowerCase()
                        const formattedComponent = this.formatComponentName(component).toLowerCase()
                        return fullPerm.includes(searchLower) || 
                               formattedPerm.includes(searchLower) || 
                               formattedComponent.includes(searchLower)
                    })
                    
                    if (matchingPerms.length > 0) {
                        filteredGroup[component] = matchingPerms
                    }
                })
                
                if (Object.keys(filteredGroup).length > 0) {
                    filtered[group] = filteredGroup
                }
            })
            
            return filtered
        },
    },
    methods: {
        formatGroupName(group) {
            return _.startCase(group)
        },

        formatComponentName(val) {
            var satelliteMatch = val.match(/^satellite::(.+)$/i)
            if (satelliteMatch) {
                return _.startCase(satelliteMatch[1].replaceAll("-", " ").replaceAll("_", " "))
            }

            // Forge service: "forge::service::{appUuid}::{serviceName}"
            var serviceMatch = val.match(/^forge::service::([a-f0-9-]+)::(.+)$/i)
            if (serviceMatch) {
                var appName = this.forgeAppMap[serviceMatch[1]] || serviceMatch[1]
                return _.startCase(appName) + ' / ' + _.startCase(serviceMatch[2])
            }

            // Forge app: "forge::{appUuid}"
            var forgeAppMatch = val.match(/^forge::([a-f0-9-]{36})$/i)
            if (forgeAppMatch) {
                return _.startCase(this.forgeAppMap[forgeAppMatch[1]] || forgeAppMatch[1])
            }

            var cleaned = val.replace(/:[0-9a-f-]+$/i, '')
            return _.startCase(cleaned.replaceAll("thunder", "").replaceAll("::", " ").replaceAll("_", " "))
        },

        formatPermissionName(perm) {
            return perm.replace("function::", "").replaceAll("_", " ").replaceAll("::", " ")
        },

        getGroupPermCount(group) {
            const groupPerms = this.permissionsList[group] || {}
            return Object.values(groupPerms).flat().length
        },

        hasSelectedPerms(group, component) {
            const groupPerms = this.permissionsList[group] || {}
            const permList = groupPerms[component] || []
            return permList.some(perm => this.isPermSelected(perm, component))
        },

        getSelectedCount(group, component, permList) {
            return permList.filter(perm => this.isPermSelected(perm, component)).length
        },

        isPermSelected(perm, component) {
            return this.perms.indexOf(`${perm} ${component}`) !== -1
        },

        areAllSelected(group, component, permList) {
            return permList.every(perm => this.isPermSelected(perm, component))
        },

        isSomeSelected(group, component, permList) {
            const selected = permList.filter(perm => this.isPermSelected(perm, component)).length
            return selected > 0 && selected < permList.length
        },

        togglePerm(perm, component) {
            const fullPerm = `${perm} ${component}`
            const index = this.perms.indexOf(fullPerm)
            if (index === -1) {
                this.perms.push(fullPerm)
            } else {
                this.perms.splice(index, 1)
            }
        },

        toggleAllComponentPerms(group, component, permList) {
            const allSelected = this.areAllSelected(group, component, permList)
            permList.forEach(perm => {
                const fullPerm = `${perm} ${component}`
                const index = this.perms.indexOf(fullPerm)
                if (allSelected && index !== -1) {
                    this.perms.splice(index, 1)
                } else if (!allSelected && index === -1) {
                    this.perms.push(fullPerm)
                }
            })
        },

        removePermFromList(perm) {
            const index = this.perms.indexOf(perm)
            if (index !== -1) {
                this.perms.splice(index, 1)
            }
        },

        clearAllPerms() {
            this.$mainDialog.msgBoxConfirm(this.$t('confirmClearAllPermissions')).then(confirmed => {
                if (confirmed) {
                    this.perms = []
                }
            })
        },

        selectAllPermissions() {
            const allPerms = []
            Object.keys(this.permissionsList).forEach(group => {
                const groupPerms = this.permissionsList[group]
                Object.keys(groupPerms).forEach(component => {
                    groupPerms[component].forEach(perm => {
                        const fullPerm = `${perm} ${component}`
                        if (!allPerms.includes(fullPerm)) {
                            allPerms.push(fullPerm)
                        }
                    })
                })
            })
            this.perms = allPerms
        },

        save() {
            this.$http.post("/v1/user/role/create", { name: this.name, level: this.level, permissions: this.perms, workspaces: this.spaces, tags: this.tags }).then(() => {

                this.$bvToast.toast("Role created", {
                    variant: 'success',
                    title: "Success"
                })
            }).catch((err) => {
                this.$bvToast.toast(this.$t('unexpectedError'), {
                    variant: 'danger',
                    title: this.$t('error')
                })

                console.error(err)

            })
        },

        tabsScroller() {
            const tabs = this.$refs.permissionsTabs
            const root = tabs && (tabs.$el || tabs)
            return root ? root.querySelector('.tabs-nav') : null
        },

        attachTabsScrollListener() {
            const wrapper = this.tabsScroller()
            if (!wrapper) return null

            if (this._tabsScrollElement !== wrapper) {
                if (this._tabsScrollElement) {
                    this._tabsScrollElement.removeEventListener('scroll', this.handleTabScroll)
                }
                wrapper.addEventListener('scroll', this.handleTabScroll)
                this._tabsScrollElement = wrapper
            }

            return wrapper
        },

        scrollTabs(direction) {
            const wrapper = this.attachTabsScrollListener()
            if (!wrapper) return
            
            const scrollAmount = 200
            const currentScroll = wrapper.scrollLeft
            
            if (direction === 'left') {
                wrapper.scrollTo({
                    left: currentScroll - scrollAmount,
                    behavior: 'smooth'
                })
            } else {
                wrapper.scrollTo({
                    left: currentScroll + scrollAmount,
                    behavior: 'smooth'
                })
            }
        },

        handleTabScroll() {
            const wrapper = this.tabsScroller()
            if (!wrapper) return
            
            this.showLeftArrow = wrapper.scrollLeft > 0
            this.showRightArrow = wrapper.scrollLeft < (wrapper.scrollWidth - wrapper.clientWidth - 1)
        },

        checkTabsOverflow() {
            this.$nextTick(() => {
                const wrapper = this.attachTabsScrollListener()
                if (!wrapper) return
                
                this.showRightArrow = wrapper.scrollWidth > wrapper.clientWidth
                this.handleTabScroll()
            })
        }
    },
    mounted() {
        this.$store.dispatch("fetchAllForgeApps")
        this.$store.dispatch("fetchPermissionsList").then(() => {
            this.loading = false
            this.checkTabsOverflow()
        }).catch(() => {
            this.loading = false
        })
    },
    beforeDestroy() {
        if (this._tabsScrollElement) {
            this._tabsScrollElement.removeEventListener('scroll', this.handleTabScroll)
        }
    }
}
</script>

<style scoped lang="scss">
@import '@/assets/scss/_custom.scss';

.role-edit-container {
    max-width: 1400px;
    margin: 0 auto;
}

.role-basic-info {
    h4 {
        color: var(--text-primary);
        font-weight: 600;
    }
}

.permissions-card {
    .permissions-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        
        h4 {
            color: var(--text-primary);
            font-weight: 600;
            margin: 0;
        }
        
        .header-actions {
            display: flex;
            align-items: center;
            gap: 8px;
            
            .badge {
                font-size: 14px;
                padding: 8px 16px;
            }
        }
    }
}

.permissions-tabs {
    width: 100%;

    ::v-deep .tabs-nav {
        overflow-x: auto;
        overflow-y: hidden;
        padding: 0 44px;
        scrollbar-width: none; /* Firefox */
        -ms-overflow-style: none; /* IE and Edge */
    }

    ::v-deep .tabs-nav::-webkit-scrollbar {
        display: none; /* Chrome, Safari, Opera */
    }

    ::v-deep .nav-tabs {
        border-bottom: 2px solid $dark-border2;
        flex-wrap: nowrap !important;
        
        .nav-link {
            border: none;
            border-bottom: 3px solid transparent;
            color: var(--text-secondary);
            padding: 12px 20px;
            font-weight: 500;
            transition: all 0.2s;
            white-space: nowrap;
            
            &:hover {
                color: var(--text-primary);
                border-bottom-color: rgba(102, 126, 234, 0.5);
            }
            
            &.active {
                background: transparent;
                color: $protokol-royal-active;
                border-bottom-color: $protokol-royal-active;
                
                .badge {
                    color: $protokol-royal-active !important;
                }
            }
        }
    }

    ::v-deep .tab-content {
        overflow: visible;
    }
    
    .tab-title {
        display: flex;
        align-items: center;
        gap: 8px;
    }
}

.tabs-container {
    position: relative;
    display: block;
}

.scroll-button {
    position: absolute;
    top: 6px;
    z-index: 2;
    width: 36px;
    height: 36px;
    border: 1px solid $dark-border2;
    background: $dark-hard2-background;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s;
    color: var(--text-secondary);
    
    &:hover {
        background: $dark-hard-background;
        border-color: $protokol-royal-active;
        color: $protokol-royal-active;
    }
    
    &.scroll-left {
        left: 0;
    }
    
    &.scroll-right {
        right: 0;
    }
}

.components-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
    gap: 20px;
}

.component-card {
    background: $dark-hard2-background;
    border: 1px solid $dark-border2;
    border-radius: 12px;
    overflow: hidden;
    transition: all 0.2s;
    
    &.has-selected {
        border-color: $protokol-royal-active;
        box-shadow: 0 0 0 1px rgba(102, 126, 234, 0.3);
    }
    
    &:hover {
        border-color: $dark-border;
    }
    
    .component-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 16px;
        background: $dark-background;
        border-bottom: 1px solid $dark-border2;
        cursor: pointer;
        
        &:hover {
            background: $dark-hard-background;
        }
        
        .component-info {
            flex: 1;
            
            .component-name {
                margin: 0;
                font-size: 14px;
                font-weight: 600;
                color: var(--text-primary);
            }
            
            .component-count {
                font-size: 12px;
                color: var(--text-muted);
            }
        }
        
        .select-all-checkbox {
            margin: 0;
        }
    }
    
    .permissions-list {
        padding: 12px;
        
        .permission-item {
            padding: 8px 12px;
            border-radius: 6px;
            cursor: pointer;
            transition: background 0.2s;
            
            &:hover {
                background: $dark-background;
            }
            
            .permission-checkbox {
                margin: 0;
                
                .permission-label {
                    font-size: 13px;
                    color: var(--text-primary);
                    text-transform: capitalize;
                }
            }
        }
    }
}

.selected-permissions {
    margin-top: 32px;
    padding: 24px;
    background: $dark-hard2-background;
    border-radius: 12px;
    border: 1px solid $dark-border2;
    
    .summary-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 16px;
        
        h5 {
            margin: 0;
            color: var(--text-primary);
            font-weight: 600;
        }
    }
    
    .permissions-badges {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        
        .perm-badge {
            padding: 8px 12px;
            font-size: 12px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s;
            display: inline-flex;
            align-items: center;
            
            &:hover {
                background: #dc3545;
                transform: scale(1.05);
            }
        }
    }
}

::v-deep .custom-control-label {
    cursor: pointer;
}

.loading-state {
    .component-card-skeleton {
        background: $dark-hard2-background;
        border: 1px solid $dark-border2;
        border-radius: 12px;
        padding: 16px;
    }
}
</style>
